/**
 * Where a portalled floating panel goes, given the rect of the thing that
 * opened it.
 *
 * Shared by BdrSelectPopup (a field's value list) and BdrMenu (a menu of
 * actions). They look different on purpose and behave the same on purpose:
 * both are `position: fixed` children of <body>, because anything rendered
 * inline gets clipped by the first ancestor with `overflow` — and in this app
 * that ancestor is usually a modal body.
 *
 * Kept in one file rather than written twice. Two copies of positioning maths
 * drift, and the drift shows up as "the menu is fine but the dropdown runs off
 * the screen", which is exactly the class of bug this style pass has spent its
 * time removing.
 */

export const ANCHOR_GAP = 4;
export const VIEWPORT_MARGIN = 8;

/**
 * @param rect      the anchor's getBoundingClientRect()
 * @param maxHeight the tallest the panel may be. Default: no cap, so the
 *                  panel grows until it reaches the bottom of the window and
 *                  only then scrolls. A fixed cap makes a menu opened near the
 *                  TOP of a tall window scroll for no reason, with half the
 *                  screen empty below it.
 * @param minHeight the shortest a panel can be and still be worth opening —
 *                  below this, flipping above the anchor is better than
 *                  squeezing under it
 * @param width     "match" pins the panel to the anchor's width (right for a
 *                  field's list); a number is a minimum and the panel sizes
 *                  to its content (right for a menu, whose labels have
 *                  nothing to do with how wide its trigger happens to be)
 */
export function placeAgainstAnchor(rect, { maxHeight = Infinity, minHeight = 120, width = "match" } = {}) {
  const below = window.innerHeight - rect.bottom - ANCHOR_GAP - VIEWPORT_MARGIN;
  const above = rect.top - ANCHOR_GAP - VIEWPORT_MARGIN;

  // Prefer below; flip only when below genuinely cannot hold a usable panel
  // AND above is roomier. Flipping for a few pixels is worse than scrolling.
  const flip = below < minHeight && above > below;
  const room = Math.max(flip ? above : below, minHeight);

  const style = {
    maxHeight: Math.min(maxHeight, room),
    // Anchoring by `bottom` when flipped means the panel grows upward from the
    // anchor without needing its rendered height first.
    ...(flip
      ? { bottom: window.innerHeight - rect.top + ANCHOR_GAP }
      : { top: rect.bottom + ANCHOR_GAP }),
  };

  if (width === "match") {
    return { ...style, left: rect.left, width: rect.width };
  }

  // Content-sized. Clamp the left edge so a panel opened near the right edge
  // of the window does not start off-screen; `max-width` in CSS stops the
  // other end running off.
  const minWidth = typeof width === "number" ? width : 0;
  const maxLeft = window.innerWidth - minWidth - VIEWPORT_MARGIN;
  return {
    ...style,
    left: Math.max(VIEWPORT_MARGIN, Math.min(rect.left, maxLeft)),
    minWidth,
  };
}
